import React, { useEffect, useState } from "react";
import { api } from "../../../software/api";
import { MapPin, AlertTriangle, Store, Loader2, CalendarPlus, Sparkles } from "lucide-react";
import { todayISO, dayLabel, firstName } from "./boardUtils";

const Flag = ({ children, tone }) => <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${tone}`}>{children}</span>;

export const SuggestDayPanel = ({ clientId, address, onBook, bookLabel = "Book here" }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!clientId && !address) return;
    setD(null); setErr("");
    api("POST", "/route/suggest", { client_id: clientId || null, address: address || null, start: todayISO(), days: 21 })
      .then(setD).catch((e) => setErr(e.message));
  }, [clientId, address]);

  if (err) return <p className="text-xs text-red-600" data-testid="suggest-error">{err}</p>;
  if (!d) return <p className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="suggest-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" />Finding the best day…</p>;
  return (
    <div className="rounded-xl border-2 border-sky-200 bg-sky-50/60 p-2.5" data-testid="suggest-day-panel">
      <p className="text-xs font-black text-sky-800 mb-1.5 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" />Best days nearby{d.target.suburb ? ` · ${d.target.suburb}` : ""}</p>
      {d.pending_geocode > 0 && <p className="text-[10px] text-slate-500 mb-1">Still locating {d.pending_geocode} addresses — suggestions get sharper next time.</p>}
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {d.suggestions.map((s, i) => (
          <div key={`${s.tech}-${s.date}`} className={`flex items-center gap-2 rounded-lg bg-white border px-2 py-1.5 ${i === 0 ? "border-emerald-400" : "border-slate-200"}`} data-testid="suggest-row">
            <div className="min-w-0 flex-1 text-[12px] leading-tight">
              <p className="font-bold text-slate-900">{firstName(s.tech)} · {dayLabel(s.date)} <span className="font-semibold text-slate-500">· {s.jobs} job{s.jobs === 1 ? "" : "s"}</span></p>
              <p className="text-slate-600 truncate">
                <MapPin className="inline h-3 w-3 -mt-0.5" />{" "}
                {s.free_day ? "Free day — no jobs booked yet" : s.km != null ? `${s.km} km from ${s.near_client || s.near_suburb}` : `In ${s.suburbs.join(" · ") || "unlocated suburbs"}`}
              </p>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {i === 0 && <Flag tone="bg-emerald-100 text-emerald-700">Best match</Flag>}
                {s.full && <Flag tone="bg-red-100 text-red-700"><AlertTriangle className="inline h-3 w-3 -mt-0.5" /> Full day</Flag>}
                {s.away.length > 0 && <Flag tone="bg-rose-100 text-rose-700">Away: {s.away[0]}</Flag>}
                {s.shop_saturday && <Flag tone="bg-slate-200 text-slate-700"><Store className="inline h-3 w-3 -mt-0.5" /> Shop Saturday</Flag>}
              </div>
            </div>
            <button onClick={() => onBook(s.tech, s.date)} data-testid={`suggest-book-${firstName(s.tech).toLowerCase()}-${s.date}`}
              className="shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold px-3 py-1.5 inline-flex items-center gap-1"><CalendarPlus className="h-3.5 w-3.5" />{bookLabel}</button>
          </div>
        ))}
      </div>
    </div>
  );
};
